import React from 'react';
import PropTypes from 'prop-types';
import { ToastContainer } from 'react-toastify';
import Navbar from 'components/PartnerPage/Navbar';
import Footer from 'components/PartnerPage/Footer';
import ShipOverview from 'components/PartnerPage/ShipOverview';
import FeaturesGrid from 'components/PartnerPage/FeaturesGrid';
import Newsletter from 'components/PartnerPage/Newsletter';
import ShipAndPlayPromise from 'components/PartnerPage/ShipPlayPromise';

// New template components
import HeroSection from 'components/PartnerPage2/HeroSection';
import JourneySection from 'components/PartnerPage2/JourneySection';
import StepsSection from 'components/PartnerPage2/StepsSection';
import ExpectationsSection from 'components/PartnerPage2/ExpectationsSection';
import CTASection from 'components/PartnerPage2/CTASection';
import ContactSection from 'components/PartnerPage2/ContactSection';


import { QuickQuoteProvider } from 'contexts/QuickQuoteContext';
import parse from 'html-react-parser';
import ShippingFlow from 'components/ShippingFlow';
import { QueryClient, QueryClientProvider } from 'react-query';
import {PartnerConfig} from 'components/ShippingFlow/constants/types';
import VideoSection from 'components/PartnerPage2/VideoSection';

const generateCTAPath = (couponCode, defaultPath) => {
  // Preserve existing URL params (TAC params, agent_id, etc.)
  const currentParams = new URLSearchParams(window.location.search);
  const newParams = new URLSearchParams();

  // Preserve TAC and other important params
  const preserveKeys = [
    'agent_id', 'ta_name', 'ta_email', 'ta_network', 'ta_company',
    'ta_agency', 'iata_number', 'utm_source', 'utm_medium', 'utm_campaign'
  ];

  preserveKeys.forEach(key => {
    const value = currentParams.get(key);
    if (value) {
      newParams.set(key, value);
    }
  });

  // Add or override coupon code
  if (couponCode) {
    newParams.set('coupon', couponCode);
  }

  const basePath = couponCode ? '/ship' : (defaultPath || '/pricing');
  const queryString = newParams.toString();
  return queryString ? `${basePath}?${queryString}` : basePath;
};

const queryClient = new QueryClient();

function PremiumPartnerPage({
  partnerId,
  partnerConfig
}) {
  const [isBooking, setIsBooking] = React.useState(false);
  // Check if using new template structure
  const useNewTemplate = partnerConfig.template === 'modern' || partnerConfig.sections;

  if (useNewTemplate) {
    return (
      <>
        <ToastContainer />
        
        {/* Dynamic Sections */}
        {partnerConfig.sections?.map((section, index) => {
          if (isBooking && section.type !== 'hero') {
            return null; // Skip non-hero sections when booking
          } 
          switch (section.type) {
            case 'hero':
              return (
                <HeroSection
                  partnerConfig={partnerConfig}
                  generateCTAPath={generateCTAPath}
                  key={index}
                  title={section.title}
                  subtitle={section.subtitle}
                  backgroundImage={section.backgroundImage}
                  backgroundPosition={section.backgroundPosition}
                  customClass={section.customClass}
                  logoURL={partnerConfig.logoURL}
                  isBooking={isBooking}
                >
                  {section.showQuickQuote && (
                    <QueryClientProvider client={queryClient}>
                      <QuickQuoteProvider allowedLocations={window.gon?.allowedLocations}>
                        <ShippingFlow
                          partnerConfig={partnerConfig}
                          flowVariant="forward"
                          onStartBooking={() => setIsBooking(true)}
                          onExitBooking={() => {
                            setIsBooking(false);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                        />
                      </QuickQuoteProvider>
                    </QueryClientProvider>
                  )}
                </HeroSection>
              );

            case 'journey':
              return (
                <JourneySection
                  key={index}
                  title={section.title}
                  description={section.description}
                  image={section.image}
                  imageAlt={section.imageAlt}
                  features={section.features}
                  customClass={section.customClass}
                />
              );

            case 'steps':
              return (
                <StepsSection
                  key={index}
                  title={section.title}
                  subtitle={section.subtitle}
                  steps={section.steps}
                  customClass={section.customClass}
                  backgroundColor={section.backgroundColor}
                />
              );

            case 'expectations':
              return (
                <ExpectationsSection
                  key={index}
                  title={section.title}
                  subtitle={section.subtitle}
                  expectations={section.expectations}
                  customClass={section.customClass}
                  backgroundColor={section.backgroundColor}
                />
              );

            case 'video':
              return (
                <VideoSection
                  key={index}
                  title={section.title}
                  description={section.description}
                  buttonText={section.buttonText}
                  videoUrl={section.videoUrl}
                  videoId={section.videoId}
                  backgroundColor={section.backgroundColor}
                  customClass={section.customClass}
                />
              );

            case 'cta':
              return (
                <CTASection
                  key={index}
                  title={section.title}
                  subtitle={section.subtitle}
                  buttonText={section.buttonText}
                  buttonLink={generateCTAPath(partnerConfig.couponCode, section.buttonLink)}
                  backgroundImage={section.backgroundImage}
                  backgroundPosition={section.backgroundPosition}
                  customClass={section.customClass}
                  onButtonClick={() => {
                    setIsBooking(true);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                />
              );

            case 'contact':
              return (
                <ContactSection
                  key={index}
                  title={section.title}
                  description={section.description}
                  contactInfo={section.contactInfo}
                  customClass={section.customClass}
                  backgroundColor={section.backgroundColor}
                />
              );

            case 'shipOverview':
              return (
                <ShipOverview
                  key={index}
                  buttonCTAPath={generateCTAPath(partnerConfig.couponCode, partnerConfig.defaultCTAPath)}
                  reasons={section.reasons}
                  bookNow={false}
                  customClass={section.customClass}
                  image={section.image}
                >
                  <p className={`text-[28px] lg:text-[32px] lg:leading-[58px] font-bold text-[#1C2938] mb-5 ${partnerConfig.path}`}>
                    {section.title}
                    {section.showLogo && (
                      <img
                        src={partnerConfig.images.shipPlayLogo}
                        alt="Ship & Play Logo"
                        className="inline-block w-[166px] ml-2"
                      />
                    )}
                  </p>
                  {section.description.map((paragraph, pIndex) => (
                    <p key={pIndex} className="text-[#231F20] text-lg leading-[30px] mt-7 first:mt-0">
                      {parse(paragraph)}
                    </p>
                  ))}
                </ShipOverview>
              );

            case 'features':
              return (
                <FeaturesGrid
                  key={index}
                  header={section.header}
                  features={section.items}
                  customClass={section.customClass}
                />
              );

            default:
              return null;
          }
        })}

        {/* Footer */}
        <Footer
          FooterLogo={partnerConfig.images.footerLogo}
          logoSize={partnerConfig.footerLogoSize}
          brand={partnerConfig.brand}
          shipLinkPath={generateCTAPath(partnerConfig.couponCode, partnerConfig.defaultCTAPath)}
          subdomain={undefined}
          customClass={partnerConfig.footer.customClass}
          supportPhoneNumber={partnerConfig.supportPhoneNumber || '(855) 792-7529'}
        >
          {partnerConfig.footer.showTerms && (
            <div className="text-left pb-8 lg:py-8 pl-[5%] lg:pl-0 gap-12">
              <div className="flex items-center justify-between">
                <h5 className="text-white text-[22px] font-semibold mb-2">
                  {partnerConfig.footer.termsTitle}
                </h5>
              </div>
              <p className="my-2 text-lg leading-8">
                {partnerConfig.termsConditionsText || partnerConfig.footer.defaultTerms}
              </p>
            </div>
          )}
          {partnerConfig.footer.showNewsletter && !isBooking && <Newsletter />}
        </Footer>
      </>
    );
  }

  // Fallback to original template structure
  const backgroundImageStyles = {
    '--background-image-mobile': `linear-gradient(rgba(0, 0, 0, 0.7), 
      rgba(0, 0, 0, 0)), url(${partnerConfig.images.backgroundMobile})`,
    '--background-image-desktop': `linear-gradient(rgba(0, 0, 0, 0.7), 
      rgba(0, 0, 0, 0)), url(${partnerConfig.images.backgroundDesktop})`,
    backgroundPosition: `${partnerConfig.images.heroBackgroundPosition || '0 0'}`,
    minHeight: isBooking ? 'auto' : '1348px',
  };

  return (
    <>
      <ToastContainer />
      <div style={backgroundImageStyles} className="relative font-poppins bg-cover bg-center backgroundImage">
        <Navbar
          NavLogo={partnerConfig.images.navLogo}
          subdomain={partnerConfig.subdomain || undefined}
          logoClasses={partnerConfig.logoClasses}
          logoURL={partnerConfig.logoURL}
          customCTAPath={generateCTAPath(partnerConfig.couponCode, partnerConfig.defaultCTAPath)}
          showExtraLinks={partnerConfig.showNavExtraLinks}
          defaultMenuItems={partnerConfig.defaultMenuItems}
          shipNow={false}
          isBooking={isBooking}
        />
        
        {isBooking === false && (
          <>
            <div className={`mb-12 w-full flex flex-col justify-center items-center ${partnerConfig.path}`}>
              <h1 className="
                  text-[2.5rem] lg:text-[2.7rem] leading-[3.125rem] lg:leading-[3.875rem]
                  text-white font-bold mb-6 break-words
                ">
                  {parse(partnerConfig.hero.header)}
              </h1>
              <p className="font-semibold text-white text-lg mb-7">
                {parse(partnerConfig.hero.subheader)}
              </p>
            </div>
          </>
        )}
          <QueryClientProvider client={queryClient}>
            <QuickQuoteProvider allowedLocations={window.gon?.allowedLocations}>
              <ShippingFlow
                partnerConfig={partnerConfig}
                flowVariant="forward"
                onStartBooking={() => setIsBooking(true)}
                onExitBooking={() => {
                  setIsBooking(false);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            </QuickQuoteProvider>
          </QueryClientProvider>
      </div>

      {!isBooking && (
        <main className="flex flex-col items-center justify-between w-full font-poppins">
          <ShipOverview
            buttonCTAPath={generateCTAPath(partnerConfig.couponCode, partnerConfig.defaultCTAPath)}
            reasons={partnerConfig.shipOverview.reasons}
            bookNow={false}
            customClass={partnerConfig.shipOverview.customClass}
          >
            <p className={`text-[28px] lg:text-[32px] lg:leading-[58px] font-bold text-[#1C2938] mb-5 ${partnerConfig.path}`}>
              {partnerConfig.shipOverview.title}
              {partnerConfig.shipOverview.showLogo && (
                <img
                  src={partnerConfig.images.shipPlayLogo}
                  alt="Ship & Play Logo"
                  className="inline-block w-[166px] ml-2"
                />
              )}
            </p>
            {partnerConfig.shipOverview.description.map((paragraph, index) => (
              <p key={index} className="text-[#231F20] text-lg leading-[30px] mt-7 first:mt-0">
                {parse(paragraph)}
              </p>
            ))}
          </ShipOverview>

          <FeaturesGrid
            header={partnerConfig.features.header}
            features={partnerConfig.features.items}
            customClass={partnerConfig.features.customClass}
          />

          {partnerConfig.showShipPlayPromise && (
            <ShipAndPlayPromise
              buttonCTAPath={generateCTAPath(partnerConfig.couponCode, partnerConfig.defaultCTAPath)}
              customClass={partnerConfig.shipPlayPromise?.customClass}
            />
          )}
        </main>
      )}

      <Footer
        FooterLogo={partnerConfig.images.footerLogo}
        logoSize={partnerConfig.footerLogoSize}
        brand={partnerConfig.brand}
        shipLinkPath={generateCTAPath(partnerConfig.couponCode, partnerConfig.defaultCTAPath)}
        subdomain={undefined}
        customClass={partnerConfig.footer.customClass}
        supportPhoneNumber={partnerConfig.supportPhoneNumber || '(855) 792-7529'}
      >
        {partnerConfig.footer.showTerms && (
          <div className="text-left pb-8 lg:py-8 pl-[5%] lg:pl-0 gap-12">
            <div className="flex items-center justify-between">
              <h5 className="text-white text-[22px] font-semibold mb-2">
                {partnerConfig.footer.termsTitle}
              </h5>
            </div>
            <p className="my-2 text-lg leading-8">
              {partnerConfig.termsConditionsText || partnerConfig.footer.defaultTerms}
            </p>
          </div>
        )}
        {partnerConfig.footer.showNewsletter && !isBooking && <Newsletter />}
      </Footer>
    </>
  )
}

PremiumPartnerPage.propTypes = {
  partnerId: PropTypes.string.isRequired,
  partnerConfig: PropTypes.shape({
    template: PropTypes.string,
    brand: PropTypes.string,
    subdomain: PropTypes.string,
    couponCode: PropTypes.string,
    termsConditionsText: PropTypes.string,
    defaultCTAPath: PropTypes.string,
    showNavExtraLinks: PropTypes.bool,
    defaultMenuItems: PropTypes.array,
    logoClasses: PropTypes.string,
    logoURL: PropTypes.string,
    supportPhoneNumber: PropTypes.string,
    sections: PropTypes.arrayOf(PropTypes.shape({
      type: PropTypes.string.isRequired,
      title: PropTypes.string,
      subtitle: PropTypes.string,
      description: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string)
      ]),
      backgroundImage: PropTypes.string,
      backgroundPosition: PropTypes.string,
      customClass: PropTypes.string,
      backgroundColor: PropTypes.string,
      showQuickQuote: PropTypes.bool,
      image: PropTypes.string,
      imageAlt: PropTypes.string,
      features: PropTypes.array,
      steps: PropTypes.array,
      expectations: PropTypes.array,
      buttonText: PropTypes.string,
      buttonLink: PropTypes.string,
      onButtonClick: PropTypes.func,
      contactInfo: PropTypes.object,
      reasons: PropTypes.array,
      showLogo: PropTypes.bool,
      header: PropTypes.string,
      items: PropTypes.array
    })),
    images: PropTypes.shape({
      heroBackgroundPosition: PropTypes.string,
      backgroundMobile: PropTypes.string,
      backgroundDesktop: PropTypes.string,
      navLogo: PropTypes.string.isRequired,
      footerLogo: PropTypes.string.isRequired,
      shipPlayLogo: PropTypes.string
    }).isRequired,
    hero: PropTypes.shape({
      header: PropTypes.string.isRequired,
      subheader: PropTypes.string.isRequired,
      quickQuoteTitle: PropTypes.string,
      quickQuoteShipFromTitle: PropTypes.string,
      quickQuoteShipToTitle: PropTypes.string,
      breakText: PropTypes.string.isRequired,
      buttonText: PropTypes.string.isRequired,
      customClass: PropTypes.string
    }),
    shipOverview: PropTypes.shape({
      title: PropTypes.string.isRequired,
      showLogo: PropTypes.bool,
      reasons: PropTypes.arrayOf(PropTypes.shape({
        title: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired
      })).isRequired,
      description: PropTypes.arrayOf(PropTypes.string).isRequired,
      customClass: PropTypes.string
    }),
    features: PropTypes.shape({
      header: PropTypes.string.isRequired,
      items: PropTypes.arrayOf(PropTypes.shape({
        image: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired
      })).isRequired,
      customClass: PropTypes.string
    }),
    showShipPlayPromise: PropTypes.bool,
    shipPlayPromise: PropTypes.shape({
      customClass: PropTypes.string
    }),
    footer: PropTypes.shape({
      showTerms: PropTypes.bool,
      termsTitle: PropTypes.string,
      defaultTerms: PropTypes.string,
      showNewsletter: PropTypes.bool,
      customClass: PropTypes.string,
      supportPhoneNumber: PropTypes.string,
    }).isRequired,
    footerLogoSize: PropTypes.string
  }).isRequired
};

export default PremiumPartnerPage;
